import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import React, { useState, useEffect, useContext, type ChangeEvent } from "react";
import HeaderAppBar from "./appbar";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AuthContext, BackendBaseUrl, ORANGE, BLACK, PREMIUM_SHADOW, BG_LIGHT } from "./global_varibles";
import PercentIcon from "@mui/icons-material/Percent";
import { alpha } from "@mui/material/styles";
import { OrderSummary } from "./order_summary";

// ---- Types ----
type StructuredCategoryInfo = {
  type_id: string | number;
  serv_id: string | number;
  item_price: number;
};
type StructuredItem = {
  item_name: string;
  item_url: string;
  categories: Record<string, StructuredCategoryInfo>;
};
type StructuredDataLS = {
  order_data: Record<string, Record<string, StructuredItem>>;
};

type StoredSubCategory = {
  quantity: number;
  price: number;
  ord_item_id?: string | number;
  [k: string]: unknown;
};
type StoredItems = Record<string, Record<string, Record<string, StoredSubCategory>>>;

type OrderedCategory = {
  ord_item_id: string | number | null;
  item_price: number;
  quantity: number;
  type_id: string | number;
  serv_id: string | number;
};
type OrderedItem = {
  item_name: string;
  item_url: string;
  categories: Record<string, OrderedCategory>;
};
type OrderedDetails = Record<string, Record<string, OrderedItem>>;

type NewItem = {
  itemName: string;
  quantity: number | string;
  unitPrice: number | string;
  totalPrice: number | string;
};

// small helper to parse LS safely
function getLS<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function ShowUpdatedOrderedDetails() {
  const navigate = useNavigate();
  const { user_data } = useContext(AuthContext as unknown as React.Context<any>) as any;

  const [orderedDetails, setOrderedDetails] = useState<OrderedDetails>({});
  const [newItems, setNewItems] = useState<NewItem[]>([]);
  const [finalTotal, setFinalTotal] = useState<number>(0);
  const [Total, setTotal] = useState<number>(0);
  const [discount, setDiscount] = useState<number>(() => {
    return getLS<number>("discount", 0);
  });
  const [otherCharges, setOtherCharges] = useState<number>(() => {
    return getLS<number>("other_charges", 0);
  });

  useEffect(() => {
    const storedItems = getLS<StoredItems>("edit_current_order_items", {} as StoredItems);
    const structuredData = getLS<StructuredDataLS>("structuredData", {} as unknown as StructuredDataLS);
    const new_items = getLS<NewItem[]>("edit_new_items", []);
    setNewItems(new_items);

    if (storedItems && (structuredData as any).order_data) {
      const order_data = (structuredData as StructuredDataLS).order_data;
      const ordered_details: OrderedDetails = {};

      Object.entries(storedItems).forEach(([category, categoryItems]) => {
        Object.entries(categoryItems).forEach(([item_id, subCategories]) => {
          const itemInOrder = Object.entries(subCategories).filter(
            ([, details]) => {
              const d = details as StoredSubCategory;
              return (d.quantity ?? 0) > 0 || d.ord_item_id !== undefined;
            }
          ) as [string, StoredSubCategory][];

          if (itemInOrder.length > 0) {
            if (!ordered_details[category]) {
              ordered_details[category] = {};
            }

            if (order_data[category] && order_data[category][item_id]) {
              const itemDetails = order_data[category][item_id];
              ordered_details[category][item_id] = {
                item_name: itemDetails.item_name,
                item_url: itemDetails.item_url,
                categories: {},
              };

              itemInOrder.forEach(([subCategory, details]) => {
                const categoryDetails = itemDetails.categories[subCategory];
                if (categoryDetails) {
                  ordered_details[category][item_id].categories[subCategory] = {
                    ord_item_id: details.ord_item_id ?? null,
                    item_price: details.price,
                    quantity: details.quantity,
                    type_id: categoryDetails.type_id,
                    serv_id: categoryDetails.serv_id,
                  };
                }
              });
            } else {
              console.warn(
                `No details found for item_id: ${item_id} in category: ${category}`
              );
            }
          }
        });
      });

      setOrderedDetails(ordered_details);
    } else {
      console.log("No items found in local storage.");
    }
  }, []);

  useEffect(() => {
    let total = 0;
    Object.values(orderedDetails).forEach((items) =>
      Object.values(items).forEach((itemDetails) =>
        Object.values(itemDetails.categories).forEach((d) => {
          const details = d as OrderedCategory;
          total += details.quantity * details.item_price;
        })
      )
    );

    newItems.forEach((item) => {
      total += Number(item.totalPrice) || 0;
    });

    if (discount > 0) {
      const Total_order_value =
        total + otherCharges - (discount / 100) * (total + otherCharges);
      setFinalTotal(parseFloat(Total_order_value.toFixed(2)));
    } else {
      setFinalTotal(parseFloat((total + otherCharges).toFixed(2)));
    }
    setTotal(parseFloat(total.toFixed(2)));
  }, [orderedDetails, newItems]); // keep same deps to avoid behavior change

  const handleSubmit = async () => {
    const customer_details = getLS<Record<string, any>>("edit_user_details", {});
    if (finalTotal > Total + otherCharges) {
      alert("Please check Order Value");
      return;
    }
    const backendRoute = `${BackendBaseUrl}create-order`;
    const data_to_create = {
      customer_details,
      ord_id: customer_details["ord_id"],
      req_id: user_data?.id,
      order_item_details: orderedDetails,
      others: newItems.map(item => ({
        ...item,
        unitPrice: Number(item.unitPrice) || 0,
        quantity: Number(item.quantity) || 0,
        totalPrice: Number(item.totalPrice) || 0
      })),
      order_details: {
        total_price: Total,
        discount: discount,
        other_charges: otherCharges,
        delivered_on: customer_details["deliveryDate"],
      },
    };

    try {
      const response = await fetch(backendRoute, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data_to_create),
      });
      const response_data = await response.json();
      if (response_data.status === "success") {
        localStorage.removeItem("edit_current_order_items");
        localStorage.removeItem("structuredData");
        localStorage.removeItem("edit_user_details");
        localStorage.removeItem("edit_new_items");
        navigate(`/order-details/${response_data.order_id}`);
      } else {
        console.error("Failed to submit order", response_data.message);
      }
    } catch (error) {
      console.error("Error submitting order:", error);
    }
  };

  const handleOtherChargesChange = (e: ChangeEvent<HTMLInputElement>) => {
    const charges = Number(e.target.value);
    setOtherCharges(charges);
    setFinalTotal(Total + charges - (discount / 100) * (Total + charges));
  };

  const handleDiscountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const discountValue = Number(e.target.value);
    setDiscount(discountValue);
    const discountAmount = (discountValue / 100) * (Total + otherCharges);
    setFinalTotal(Total + otherCharges - discountAmount);
  };

  return (
    <Box sx={{ width: "100%", bgcolor: BG_LIGHT, minHeight: '100vh', pb: 8 }}>
      <HeaderAppBar />
      {orderedDetails ? (
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 1000,
            mx: "auto",
            mt: { xs: 0, sm: 3 },
            p: { xs: 2.5, sm: 4 },
            borderRadius: { xs: 0, sm: 4 },
            boxShadow: PREMIUM_SHADOW,
            backgroundColor: "#fff",
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              width: "100%",
              mb: 4
            }}
          >
            <IconButton 
              onClick={() => navigate(-1)}
              sx={{ 
                position: "absolute", 
                left: 0, 
                color: '#64748b',
                "&:hover": { color: ORANGE, bgcolor: alpha(ORANGE, 0.1) } 
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.025em' }}>
              Update Order Summary
            </Typography>
          </Box>
          
          <OrderSummary ordered_details={orderedDetails} newItems={newItems} />
          
          <Box
            sx={{
              width: "100%",
              mt: 6,
              mr: 2,
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 2.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Order Value:</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: BLACK }}>₹{Total}</Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Other Charges:</Typography>
                <TextField
                  type="number"
                  value={otherCharges}
                  onChange={handleOtherChargesChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  sx={{
                    width: 120,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      bgcolor: alpha(BG_LIGHT, 0.5),
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Discount:</Typography>
                <TextField
                  type="number"
                  value={discount}
                  onChange={handleDiscountChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"><PercentIcon fontSize="small" /></InputAdornment>,
                  }}
                  sx={{
                    width: 120,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      bgcolor: alpha(BG_LIGHT, 0.5),
                    },
                  }}
                />
              </Box>
              
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 3,
                  mt: 2,
                  pt: 2,
                  borderTop: `2px solid ${alpha(ORANGE, 0.1)}`,
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: 'flex-end'
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>Total:</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: ORANGE }}>₹{finalTotal}</Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                width: { xs: "100%", sm: "60%", md: "40%" },
                py: 2,
                borderRadius: 4,
                fontWeight: 800,
                fontSize: '1.1rem',
                textTransform: 'none',
                bgcolor: BLACK,
                color: "#ff8c00",
                boxShadow: `0 10px 25px ${alpha(BLACK, 0.3)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                "&:hover": { 
                  bgcolor: "#1a1f26",
                  transform: "translateY(-4px)",
                  boxShadow: `0 15px 35px ${alpha(BLACK, 0.4)}`,
                },
                "&::after": {
                  content: '"✓"',
                  ml: 1.5,
                  fontSize: '1.2rem',
                }
              }}
            >
              Update Order
            </Button>
          </Box>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 20 }}>
          <Typography variant="h6" sx={{ color: '#64748b' }}>Loading Order details...</Typography>
        </Box>
      )}
    </Box>
  );
}

export default ShowUpdatedOrderedDetails;
